import React, { useCallback, useMemo, useState } from "react"
import type { FC, SyntheticEvent } from "react"
import { useTranslation } from "react-i18next"

import { useFormatPersonName } from "@/formatters"
import type { User } from "@/models/user"
import { useUsers } from "@/services/user/use-users"
import { singleLineEllipsisStyles } from "@/styles/utils"
import { useDebounce } from "@uidotdev/usehooks"

import SearchIcon from "@mui/icons-material/Search"
import {
  Autocomplete,
  Box,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material"

interface UserOption {
  email: string
  id: string
  label: string
}

const disabledOptionId = "-1"

interface UserSearchLabels {
  minSearchText: string
  notFoundText: string
}

const createUsersOptions = (
  users: User[] | undefined,
  isLoading: boolean,
  isPlaceholderData: boolean,
  formatPersonName: (params: {
    firstName?: string | null
    lastName?: string | null
  }) => string,
  labels: UserSearchLabels,
  onlyOrgAdminEligibleUsers?: boolean
): UserOption[] => {
  if (isLoading) {
    return []
  }

  if (isPlaceholderData || !users) {
    return [
      {
        email: "",
        id: disabledOptionId,
        label: labels.minSearchText,
      },
    ]
  }

  const filteredUsers = users.filter((user) => {
    const shouldExcludeAdminRole =
      onlyOrgAdminEligibleUsers &&
      (user.role === "admin" || user.role === "admin_reader")

    return !shouldExcludeAdminRole
  })

  if (!filteredUsers.length) {
    return [
      {
        email: "",
        id: disabledOptionId,
        label: labels.notFoundText,
      },
    ]
  }

  const userOptions = filteredUsers.map((user) => {
    return {
      email: user.email,
      id: user.id ?? "",
      label: formatPersonName({
        firstName: user.firstName,
        lastName: user.lastName,
      }),
    }
  })

  return userOptions
}

interface UserSearchProps {
  organizationIdToExclude?: string
  onlyOrgAdminEligibleUsers?: boolean
  onChange: (selectedUser: User) => void
}

export const UserSearch: FC<UserSearchProps> = ({
  organizationIdToExclude,
  onlyOrgAdminEligibleUsers = false,
  onChange,
}) => {
  const { t } = useTranslation("common")
  const minSearchText = t("userSearch.searchMinMessage")
  const notFoundText = t("userSearch.searchNotFound")
  const placeholderText = t("userSearch.searchPlaceholder")
  const [searchText, setSearchText] = useState("")
  const debouncedSearchText = useDebounce(searchText, 250)
  const { formatPersonName } = useFormatPersonName()
  const { usersData, usersIsFetching, usersIsPlaceholderData } = useUsers({
    apiQueryOptions: {
      pageSize: 50,
      sort: { first_name: "asc" },
      where: {
        first_name: debouncedSearchText,
        ...(organizationIdToExclude === undefined
          ? {}
          : { organization_id_not_contains: organizationIdToExclude }),
      },
    },
    queryOptions: {
      enabled: debouncedSearchText.length > 2,
    },
  })

  const handleInputChange = useCallback(
    (event: SyntheticEvent, value: string, reason: string) => {
      if (reason === "reset") {
        return
      }

      setSearchText(value)
    },
    []
  )

  const handleSearchChange = useCallback(
    (event: SyntheticEvent, value: string | UserOption | null) => {
      if (!value || typeof value === "string") {
        return
      }

      const selected: User | undefined = usersData?.find(
        (user) => user.id === value.id
      )
      if (selected) {
        onChange(selected)
        setSearchText("")
      }
    },
    [onChange, usersData]
  )

  const handleTextFieldKeyDown = useCallback((event: SyntheticEvent) => {
    // Prevent parent (e.g. Menu) from interfering with search
    event.stopPropagation()
  }, [])

  const options: UserOption[] = useMemo(() => {
    return createUsersOptions(
      usersData,
      usersIsFetching,
      usersIsPlaceholderData,
      formatPersonName,
      { minSearchText, notFoundText },
      onlyOrgAdminEligibleUsers
    )
  }, [
    formatPersonName,
    minSearchText,
    notFoundText,
    usersData,
    usersIsFetching,
    usersIsPlaceholderData,
    onlyOrgAdminEligibleUsers,
  ])

  return (
    <Box>
      <Autocomplete
        filterOptions={(option) => option}
        freeSolo={true}
        getOptionDisabled={(option: UserOption) =>
          option.id === disabledOptionId
        }
        id="user-search"
        isOptionEqualToValue={(
          option: UserOption,
          value: string | UserOption
        ) => typeof value !== "string" && option.id === value.id}
        inputValue={searchText}
        loading={usersIsFetching}
        onChange={handleSearchChange}
        onInputChange={handleInputChange}
        openOnFocus
        options={options}
        renderInput={(params) => (
          <TextField
            {...params}
            fullWidth
            onKeyDown={handleTextFieldKeyDown}
            placeholder={placeholderText}
            slotProps={{
              ...params.slotProps,
              htmlInput: {
                ...params.slotProps?.htmlInput,
                "aria-label": placeholderText,
              },
              input: {
                ...params.slotProps?.input,
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              },
            }}
            variant="outlined"
          />
        )}
        renderOption={(props, option: UserOption) => {
          return (
            // eslint-disable-next-line @eslint-react/jsx-no-key-after-spread
            <li {...props} key={option.id}>
              <Stack
                sx={{
                  width: "100%",
                }}
              >
                <Box
                  sx={{
                    ...singleLineEllipsisStyles,
                  }}
                >
                  {option.label}
                </Box>
                {option.email && (
                  <Typography
                    component="div"
                    variant="body2"
                    sx={{
                      ...singleLineEllipsisStyles,
                      color: "text.secondary",
                    }}
                  >
                    {option.email}
                  </Typography>
                )}
              </Stack>
            </li>
          )
        }}
        size="small"
      />
    </Box>
  )
}
